import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

const ConfiguracionOrganizacion = () => {
  const [config, setConfig] = useState({
    nombre_organizacion: '',
    color_primario: '#3B82F6',
    color_secundario: '#1E40AF',
    mostrar_logo: false,
    logo_filename: null
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    cargarConfiguracion();
  }, []);

  const cargarConfiguracion = async () => {
    try {
      const token = localStorage.getItem('dashboard_token');
      const response = await axios.get(`${API_URL}/configuracion/organizacion`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setConfig(response.data.data);
        
        // Cargar preview del logo si existe
        if (response.data.data.logo_filename && response.data.data.mostrar_logo) {
          setLogoPreview(`${API_URL}/configuracion/organizacion/logo?t=${Date.now()}`);
        }
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error);
      toast.error('Error al cargar la configuración');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;

    // Validar tipo de archivo
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no permitido. Use PNG, JPG, SVG o WebP');
      return;
    }

    // Validar tamaño (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo no debe exceder 5MB');
      return;
    }

    setLogoFile(file);

    // Crear preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadLogo = async () => {
    if (!logoFile) {
      toast.warning('Seleccione un archivo primero');
      return;
    }

    setIsSaving(true);

    try {
      const token = localStorage.getItem('dashboard_token');
      const formData = new FormData();
      formData.append('archivo', logoFile);

      const response = await axios.post(
        `${API_URL}/configuracion/organizacion/logo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast.success('Logo subido exitosamente');
        setConfig(response.data.data);
        setLogoFile(null);
        cargarConfiguracion();
      }
    } catch (error) {
      console.error('Error al subir logo:', error);
      toast.error(error.response?.data?.detail || 'Error al subir el logo');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('¿Está seguro de eliminar el logo?')) {
      return;
    }

    setIsSaving(true);

    try {
      const token = localStorage.getItem('dashboard_token');
      const response = await axios.delete(
        `${API_URL}/configuracion/organizacion/logo`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('Logo eliminado exitosamente');
        setLogoPreview(null);
        setLogoFile(null);
        cargarConfiguracion();
      }
    } catch (error) {
      console.error('Error al eliminar logo:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar el logo');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateConfig = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const token = localStorage.getItem('dashboard_token');
      const formData = new FormData();
      formData.append('nombre_organizacion', config.nombre_organizacion || '');
      formData.append('color_primario', config.color_primario);
      formData.append('color_secundario', config.color_secundario);
      formData.append('mostrar_logo', config.mostrar_logo);

      const response = await axios.put(
        `${API_URL}/configuracion/organizacion`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast.success('Configuración actualizada exitosamente');
        setConfig(response.data.data);
      }
    } catch (error) {
      console.error('Error al actualizar configuración:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar la configuración');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Configuración de la Organización
        </h2>

        <form onSubmit={handleUpdateConfig} className="space-y-6">
          {/* Nombre de la Organización */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre de la Organización
            </label>
            <input
              type="text"
              value={config.nombre_organizacion || ''}
              onChange={(e) => setConfig({ ...config, nombre_organizacion: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ej: Mi Empresa S.A."
            />
          </div>

          {/* Logo */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Logo de la Organización</h3>
            
            <div className="space-y-4">
              {/* Preview del logo */}
              {logoPreview && (
                <div className="flex items-center space-x-4">
                  <div className="w-32 h-32 border-2 border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-2">
                      {config.logo_filename || 'Logo actual'}
                    </p>
                    <button
                      type="button"
                      onClick={handleDeleteLogo}
                      disabled={isSaving}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Eliminar Logo
                    </button>
                  </div>
                </div>
              )}

              {/* Upload de logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {logoPreview ? 'Cambiar Logo' : 'Subir Logo'}
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {logoFile && (
                    <button
                      type="button"
                      onClick={handleUploadLogo}
                      disabled={isSaving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isSaving ? 'Subiendo...' : 'Subir'}
                    </button>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Formatos permitidos: PNG, JPG, SVG, WebP. Tamaño máximo: 5MB
                </p>
              </div>

              {/* Mostrar logo */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="mostrar_logo"
                  checked={config.mostrar_logo}
                  onChange={(e) => setConfig({ ...config, mostrar_logo: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="mostrar_logo" className="ml-2 block text-sm text-gray-700">
                  Mostrar logo en el sistema
                </label>
              </div>
            </div>
          </div>

          {/* Colores */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Colores del Sistema</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color Primario
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={config.color_primario}
                    onChange={(e) => setConfig({ ...config, color_primario: e.target.value })}
                    className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.color_primario}
                    onChange={(e) => setConfig({ ...config, color_primario: e.target.value })}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="#3B82F6"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color Secundario
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={config.color_secundario}
                    onChange={(e) => setConfig({ ...config, color_secundario: e.target.value })}
                    className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.color_secundario}
                    onChange={(e) => setConfig({ ...config, color_secundario: e.target.value })}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="#1E40AF"
                  />
                </div>
              </div>
            </div>

            {/* Preview de colores */}
            <div className="mt-4 p-4 border border-gray-200 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">Vista previa:</p>
              <div className="flex space-x-4">
                <div
                  className="flex-1 h-16 rounded-lg flex items-center justify-center text-white font-semibold"
                  style={{ backgroundColor: config.color_primario }}
                >
                  Primario
                </div>
                <div
                  className="flex-1 h-16 rounded-lg flex items-center justify-center text-white font-semibold"
                  style={{ backgroundColor: config.color_secundario }}
                >
                  Secundario
                </div>
              </div>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={cargarConfiguracion}
              disabled={isSaving}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfiguracionOrganizacion;
